import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  selectIsGlobalSmsFirstPartyUIFeatureFlagOn,
  selectIsSendMmsOn,
} from 'core/userContext/selectors';
import { useSmsSendersWithESP } from 'modules/templateEditor/hooks/useSmsSendersWithESP';
import { SMSContentDefinition } from 'modules/graphql/types';
import { InfoBanner } from 'ui/banner';
import { Tooltip } from 'ui/tooltip';
import { FormField } from 'ui/formField';
import { spacing } from 'ui/theme';

import { useState } from 'react';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { displayContent } from '../../common/content';
import { UrlInputField } from '../../common/components';
import { getIsEditMode, constructFieldErrors } from '../../common/utils';
import { FieldNames, MediumDataKey } from '../../common/types';
import { constructErrorInlineText } from '../../common/ErrorInlineText';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';

const SMS_IMAGE_URL: MediumDataKey = 'imageUrl';

const StyledFormField = styled(FormField)`
  margin-top: ${spacing.plusHalf};
`;

const StyledTooltip = styled(Tooltip)`
  width: 100%;
`;

const GlobalSmsInfoBanner = styled(InfoBanner)`
  margin-top: ${spacing.threeQuarter};
`;

export const SMSMediaUrlInput = () => {
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { localeContentWarnings, localeContentErrors } = useErrorValidationContext();
  const smsSenders = useSmsSendersWithESP();
  const smsData = (liveTemplate?.mediumData || {}) as SMSContentDefinition;
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const isContactCardSet = Number.isInteger(smsData.verifiedContactCardId);
  const isGlobalSmsEnabled = useSelector(selectIsGlobalSmsFirstPartyUIFeatureFlagOn);
  const sendMmsEnabled = useSelector(selectIsSendMmsOn);
  const isSenderFirstParty = !!smsSenders[smsData.smsSender.id]?.esp?.isSenderIterable;
  const isDisabled = isContactCardSet || (!sendMmsEnabled && isSenderFirstParty);

  const [showInfoBanner, setShowInfoBanner] = useState(
    (isGlobalSmsEnabled && smsData?.imageUrl?.length) || false,
  );

  const { hasWarning: hasSMSMediaUrlInputWarning } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.IMAGE_URL,
  });
  const smsMediaUrlInputError = constructErrorInlineText({
    condition: isEditMode,
    hasWarning: hasSMSMediaUrlInputWarning,
  });

  const handleChange = (newImageUrl: string) => {
    setCurrentMediumField('imageUrl', newImageUrl);
    setShowInfoBanner(isGlobalSmsEnabled && newImageUrl.length);
  };

  const helpText = isEditModeVisible ? (
    <div>
      {displayContent.strMediaUrlHelp}
      {showInfoBanner && (
        <GlobalSmsInfoBanner>{displayContent.strMediaUrlInfoBannerMessage}</GlobalSmsInfoBanner>
      )}
    </div>
  ) : (
    ''
  );

  return (
    <StyledFormField
      data-test="sms-location-field"
      isHorizontalLayout
      helpText={helpText}
      labelText={displayContent.labelMediaUrl}
      hasWarning={hasSMSMediaUrlInputWarning}
      warningText={smsMediaUrlInputError}
    >
      <StyledTooltip
        isEnabled={isEditMode && isDisabled}
        tooltipContent={
          isContactCardSet
            ? SETTINGS_DISPLAY_CONTENT.disabledMediaUrl
            : SETTINGS_DISPLAY_CONTENT.MmsNotAvailable
        }
      >
        <UrlInputField
          id={SMS_IMAGE_URL}
          inputValue={smsData.imageUrl}
          onChangeHandler={handleChange}
          placeholderText={displayContent.placeholderMediaUrl}
          isDisabled={isDisabled}
          mediumDataKey={SMS_IMAGE_URL}
        />
      </StyledTooltip>
    </StyledFormField>
  );
};
